/**
 * Who may do what. One table, read by the navigation *and* by the server.
 *
 * This module exists because of a specific defect. In the prototype, the
 * sidebar and the route guard said Performance was for
 * `['admin', 'supervisor']`, while `performance.routes.js` said
 * `requireRole('supervisor')`. An admin therefore saw a link in their own
 * sidebar that returned 403 every time they clicked it — a dead end shipped, and
 * documented as a known mismatch rather than fixed, because the two statements
 * of the rule lived in different files and nothing forced them to agree.
 *
 * Here there is one statement. The sidebar is built by filtering on `can`, and
 * every page, Server Action and route handler authorises with `can`. A nav item
 * that renders is reachable, and a page that is reachable renders in the nav,
 * because both derive from this object rather than restating it.
 *
 * Row-level scoping is deliberately *not* here. "A supervisor sees their own
 * guards" is a query predicate, not a permission — mixing the two is how
 * permission code turns into an ORM. See `ConsoleScope` at the bottom for where
 * that lives.
 */

/** Every principal that can hold a session. Mirrors the `user_role` enum. */
export const ROLES = ["admin", "supervisor", "guard", "company"] as const;
export type Role = (typeof ROLES)[number];

/**
 * The roles a *member of staff* can hold — which is not the same set.
 *
 * `ROLES` grew a fourth member when clients gained a login, and two screens read
 * it to build a role picker: the employee form's dropdown and the employees
 * filter. Without this distinction both would offer "شركة" as a role you could
 * assign to a person, and a company principal would be creatable from the staff
 * form. Two other places were safe only by accident, on hardcoded literals; they
 * read this now so the safety is deliberate.
 */
export const EMPLOYEE_ROLES = ["admin", "supervisor", "guard"] as const;
export type EmployeeRole = (typeof EMPLOYEE_ROLES)[number];

/**
 * Every distinct thing a person can attempt. Named `resource.verb` so a reader
 * can tell at a glance what a check is protecting.
 */
export const ACTIONS = [
  /** Reach the web console at all. False for guards — they use the mobile app. */
  "console.access",

  "employees.read",
  "employees.create",
  "employees.update",
  "employees.deactivate",
  "employees.invite",
  /**
   * Send SMS, email, or an in-app notification to selected employees from the console.
   *
   * Separate from `employees.update`: messaging is an operational broadcast, not
   * an edit to the person's record. Supervisors may message their own team;
   * admins may message anyone. The notification reaches guards and supervisors
   * only — the API drops admin and client accounts.
   */
  "employees.message",

  /**
   * Decide a hiring request: approve, reject, or activate from the waiting list.
   *
   * Separate from `employees.create`, which files the request, and from
   * `employees.deactivate`, which suspends a seat somebody already holds.
   * Approving is what turns a `pending` row into an account that can sign in —
   * or, when the seat cap is reached, into a `waiting_list` one. A supervisor
   * decides for their own team; the API scopes it either way.
   */
  "employees.approve", // approval workflow

  /**
   * Read the parts of an employee's record that are not about their work.
   *
   * National ID, IBAN, date of birth, and the identity documents. Separate from
   * `employees.read` because the two answer different questions: a supervisor
   * needs to know who is on their team and reach their attendance, and has no
   * business holding their bank details. Rostering somebody is not a reason to
   * be able to pay them.
   *
   * Enforced in three places that must agree — the read strips the fields, the
   * CSV export omits the columns, and the console omits the sections. The
   * console omitting them is presentation; the other two are the control.
   */
  "employees.readSensitive",
  /** Edit the HR record: the profile and its six collections. */
  "employees.writeProfile",
  /** Configure which leave types an employee may request. */
  "employees.writeLeaveTypes",

  /**
   * Send somebody a link to set a new password.
   *
   * Named `accounts.` and not `employees.` deliberately. It acts on a *principal*
   * — anything that can sign in — so the same grant and the same action will
   * cover a client company's login when one exists, rather than an
   * `employees.resetPassword` that has to be renamed or, worse, duplicated.
   */
  "accounts.resetPassword",

  "zones.read",
  "zones.write",
  "zones.archive",
  "zones.assignGuards",

  "shifts.read",
  "shifts.write",
  "shifts.publish",

  "leave.read",
  /**
   * File a request on somebody else's behalf, from the console.
   *
   * A guard files their own from the app and needs no permission for it. This is
   * HR entering leave for an employee who did not — including a supervisor or an
   * admin, who have no mobile app at all and whose own leave had until now no
   * way into the system.
   */
  "leave.create",
  "leave.decide",
  "leave.reopen",

  "attendance.read",
  "attendance.correct",

  "tracking.read",

  "reports.read",
  "reports.export",

  "notifications.read",

  "settings.read",
  "settings.write",
  "roles.manage",
  /** Maintain the shared dropdown lists — nationalities, insurers, and the rest. */
  "lookups.manage",

  "companies.read",
  "companies.write",
  "companies.archive",

  "audit.read",

  /**
   * Review mobile device-change requests (rebind login handset).
   *
   * Admin only — mirrors the API matrix. Approving swaps the bound device id.
   */
  "deviceChanges.read",
  "deviceChanges.decide",

  "incidents.read",
  "incidents.update",

  /**
   * Schedule app maintenance windows (multi-platform).
   * Admin only — mirrors the API matrix.
   */
  "maintenance.read",
  "maintenance.write",

  /**
   * Publish app version / store update records (one platform per row).
   * Admin only — mirrors the API matrix.
   */
  "updates.read",
  "updates.write",

  /**
   * Bilingual policy pages shown in the app and on the public policy route.
   * Admin only — mirrors the API matrix.
   */
  "policies.read",
  "policies.write",

  /**
   * Monthly performance for an employee. Supervisors see their own team.
   * Mirrors the API matrix.
   */
  "performance.read",

  /**
   * Salary statements, deductions, and bonuses. Admin only.
   * Mirrors the API matrix.
   */
  "payroll.read",
  "payroll.write",
] as const;
export type Action = (typeof ACTIONS)[number];

/**
 * The matrix. Every role lists every action it is granted, explicitly.
 *
 * A missing entry denies, but `permissions.test.ts` asserts the union of all
 * three rows covers every member of `ACTIONS` — so adding an action to the list
 * fails the suite until somebody has decided who gets it. Silent default-deny is
 * safe but it is also how a feature ships invisible to the role that needed it.
 */
const GRANTS: Record<Role, readonly Action[]> = {
  admin: [
    "console.access",
    "employees.read",
    "employees.create",
    "employees.update",
    "employees.deactivate",
    "employees.invite",
    "employees.message",
    "employees.approve", // approval workflow
    "employees.readSensitive",
    "employees.writeProfile",
    "employees.writeLeaveTypes",
    /* Admin only. A guard has no console, so control of their account is control
       of their attendance record — somebody else punching in as them is the exact
       fraud the geofence exists to prevent. A supervisor who needs this asks an
       admin, which is a slower path and a deliberate one. */
    "accounts.resetPassword",
    "zones.read",
    "zones.write",
    "zones.archive",
    "zones.assignGuards",
    "shifts.read",
    "shifts.write",
    "shifts.publish",
    "leave.read",
    "leave.create",
    "leave.decide",
    "leave.reopen",
    "attendance.read",
    "attendance.correct",
    "tracking.read",
    /* The two that were the bug. An admin sees Reports in the sidebar, so an
       admin can read Reports. There is no second place to disagree. */
    "reports.read",
    "reports.export",
    "notifications.read",
    "settings.read",
    "settings.write",
    "roles.manage",
    "lookups.manage",
    "companies.read",
    "companies.write",
    "companies.archive",
    "audit.read",
    "deviceChanges.read",
    "deviceChanges.decide",
    "incidents.read",
    "incidents.update",
    "maintenance.read",
    "maintenance.write",
    "updates.read",
    "updates.write",
    "policies.read",
    "policies.write",
    "performance.read",
    "payroll.read",
    "payroll.write",
  ],

  supervisor: [
    "console.access",
    /* Read-only on people: a supervisor needs to see who is on their team and
       reach a guard's attendance, but hiring and offboarding are the admin's.

       `employees.readSensitive` is deliberately absent, and it is the reason
       that action exists. A supervisor opening one of their own guards sees the
       work record — shifts, attendance, leave, zones — and not the national ID,
       the IBAN or the scanned documents. Their CSV export omits those columns
       for the same reason.

       `employees.writeProfile` and `employees.writeLeaveTypes` are absent too:
       an entitlement is a policy decision, not a staffing one. */
    "employees.read",
    "employees.message",
    /* Deciding a hire for their own team. Read-only on the record itself still
       holds — this grant is about the account's status, not its contents, and the
       API scopes every decision to the guards reporting to them. */
    "employees.approve", // approval workflow
    /* Zones: read, and post their own guards to them — but not define them.
       The prototype gave supervisors no zone access at all, which left them
       reading "outside any zone" on the tracking board with no way to see where
       the zone actually was.

       The split is deliberate. A supervisor already builds and publishes the
       rota, so without `assignGuards` they could roster a guard into a zone the
       geofence then blocks that guard from punching at — a broken state they can
       create and cannot fix, which is the exact species of dead end this rebuild
       exists to remove. Membership is staffing, and it is scoped to their own
       team by `consoleScope` and audited on every write.

       The boundary itself, the geofence policy, the required headcount and
       archiving stay with the admin. That is the control surface: changing where
       a site *is* or whether an out-of-zone punch is accepted is a different kind
       of decision from choosing who stands the post. */
    "zones.read",
    "zones.assignGuards",
    "shifts.read",
    "shifts.write",
    "shifts.publish",
    "leave.read",
    "leave.decide",
    "leave.reopen",
    "attendance.read",
    "attendance.correct",
    "tracking.read",
    "reports.read",
    "reports.export",
    "notifications.read",
    "settings.read",
    "incidents.read",
    "incidents.update",
    "performance.read",
  ],

  /**
   * Guards have no console at all — they check in, file leave and send positions
   * from the mobile app, over `/api/mobile/v1`.
   *
   * The prototype let a guard authenticate into the web app and then dead-ended
   * them on a bare "no dashboard access" text node with a logout button, whose
   * own code comment explains it exists to stop an infinite redirect loop. An
   * empty grant list plus the `console.access` check sends them somewhere that
   * explains itself instead.
   */
  guard: [],

  /**
   * A client company: read-only, and narrow.
   *
   * Four grants, covering the four screens the client was promised — an
   * overview, the people posted to them, their sites, and those people's
   * attendance. Everything else is denied, and each denial has a reason worth
   * keeping written down, because "grant it, the scope will hold it" is how a
   * cross-tenant leak ships:
   *
   * - `leave.read` — a request carries `subtype: 'medical'` and an
   *   `attachmentKey` pointing at a medical certificate. Handing a client its
   *   guards' medical leave is the worst grant available here. It is also
   *   ill-defined: `leave_requests` snapshots `supervisor_id` and has no company
   *   of its own, so the filter would read the guard's *current* company and
   *   disagree with the snapshot on a transferred guard.
   * - `tracking.read` — `src/lib/events/bus.ts` is a firehose. `subscribe`
   *   forwards every event to every subscriber, so the SSE route would stream one
   *   client every other client's guard positions. It needs a company on the event
   *   payload before this can be granted.
   * - `reports.read` / `reports.export` — not in scope for the first release, and
   *   withholding them keeps `api/export/[kind]` off the tenant boundary.
   * - `shifts.read` — not in scope for the first release. Attendance already
   *   answers "did the post get stood".
   * - `notifications.read` — nothing in the codebase writes a notification
   *   addressed to a company principal, so the bell would be a permanently empty
   *   screen. Grant it when there is a writer.
   * - `settings.read` — Teslm's own operating configuration.
   * - every write — a client reads its operation, it does not run it.
   */
  company: [
    "console.access",
    "employees.read",
    "zones.read",
    "attendance.read",
  ],
};

const GRANT_SETS: Record<Role, ReadonlySet<Action>> = {
  admin: new Set(GRANTS.admin),
  supervisor: new Set(GRANTS.supervisor),
  guard: new Set(GRANTS.guard),
  company: new Set(GRANTS.company),
};

/** The one authorisation predicate. */
export function can(role: Role | null | undefined, action: Action): boolean {
  if (!role) return false;
  return GRANT_SETS[role]?.has(action) ?? false;
}

/** True when every listed action is granted. */
export function canAll(role: Role | null | undefined, actions: readonly Action[]): boolean {
  return actions.every((action) => can(role, action));
}

/** True when at least one listed action is granted — for a nav group heading. */
export function canAny(role: Role | null | undefined, actions: readonly Action[]): boolean {
  return actions.some((action) => can(role, action));
}

/** Everything a role may do. Exported for the settings screen and the tests. */
export function actionsFor(role: Role): readonly Action[] {
  return GRANTS[role];
}

/**
 * Row-level scope, kept separate from permission on purpose.
 *
 * `leave.read` says a supervisor may open the leave queue. This says *whose*
 * requests are in it.
 *
 * The prototype enforced this correctly but only ever for leave requests, and
 * only by hardcoding `supervisorId = me.id` inside one model function — so the
 * same rule had to be remembered again for tracking, and was forgotten for the
 * admin branch of the performance guard list, which fetched every guard in the
 * system and then 404'd on most of them.
 *
 * ## Why this is a union and not two nullable ids
 *
 * The obvious shape is `{ supervisorId: number | null; companyId: number | null }`
 * and it is quietly dangerous: a service can accept it, implement the supervisor
 * branch, ignore `companyId`, and **typecheck** — while a client company reads
 * every other client's data. Four states, one of them meaningless, and the whole
 * tenant boundary resting on every consumer remembering both.
 *
 * A discriminated union makes the compiler do that remembering. Every consumer
 * switches on `kind` with a `never` default, so a fifth variant later is a
 * compile error at every site rather than a silent fall-through.
 */
export type ConsoleScope =
  /** An admin. No restriction. */
  | { kind: "all" }
  /** A supervisor: their own guards — already narrower than their company. */
  | { kind: "supervisor"; supervisorId: number }
  /** A client company: everything belonging to that company. */
  | { kind: "company"; companyId: number }
  /** Matches nothing. A guard, or a principal whose scope cannot be resolved. */
  | { kind: "none" };

/**
 * The minimum a principal must carry to be scoped.
 *
 * Named so callers can accept it structurally instead of restating the shape — the
 * file-download route used to declare its own
 * `{ id: number; role: "admin" | "supervisor" | "guard" }`, and adding a fourth
 * role turned that hand-written union into a compile error. Which was the right
 * outcome, and this is how it stops recurring.
 */
export type SessionUserScope = {
  id: number;
  role: Role;
  companyId: number | null;
};

/**
 * The supervisor scope as a bare id, or null.
 *
 * Kept in this repository even though `consoleScope` supersedes it on the API
 * side. The service shims here accept it and deliberately **do not send it** —
 * the API derives the boundary from the session, so there is no parameter through
 * which a caller could ask for somebody else's team. Removing it would mean
 * editing thirty-one call sites to delete an argument that is already ignored,
 * which is churn rather than a fix.
 */
export function scopeToSupervisor(user: {
  id: number;
  role: Role;
}): { supervisorId: number } | null {
  return user.role === "supervisor" ? { supervisorId: user.id } : null;
}

/**
 * The same scope as `scopeToSupervisor`, as a bare id.
 *
 * Services take an id rather than an object because that is what a `where` clause
 * needs; the object form reads better at a call site that is passing it on. Both
 * derive from the one rule so they cannot disagree.
 */
export function scopeToSupervisorId(user: { id: number; role: Role }): number | null {
  return scopeToSupervisor(user)?.supervisorId ?? null;
}

/**
 * The one place a session becomes a scope.
 *
 * A supervisor gets `supervisor` and not `company` deliberately: "my own guards"
 * is a subset of "my company's people", so applying both would be redundant, and
 * applying the wider one would be a widening.
 */
export function consoleScope(user: SessionUserScope): ConsoleScope {
  switch (user.role) {
    case "admin":
      return { kind: "all" };
    case "supervisor":
      return { kind: "supervisor", supervisorId: user.id };
    case "company":
      /* Never `{ kind: "all" }` when the company is missing. A check constraint on
         `users` makes that row unrepresentable, and this is the second lock:
         reading "no company" as "no restriction" would turn one absent foreign-key
         value into a full-database disclosure. */
      return user.companyId === null
        ? { kind: "none" }
        : { kind: "company", companyId: user.companyId };
    case "guard":
      /* Guards have no console. If one ever reaches a scoped query, it returns
         nothing rather than everything. */
      return { kind: "none" };
  }
}
